import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { InstitutionTypesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsInstitutionsSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as InstitutionTypeActions from "../../../actions/institutionTypes";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { InstitutionLookupsLayout } from "../../index";

import "./InstitutionTypesPage.scss";

interface InstitutionTypesPageProps {
    formFilter: IFormFilter;
    institutionType: Dtos.InstitutionType[];
    loadingInstitutionTypes: boolean;
    loadInstitutionTypesSuccess: boolean;
    loadInstitutionTypesFailure: boolean;
    loadInstitutionTypesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearInstitutionTypes: InstitutionTypeActions.IInstitutionTypeClearActionCreator;
    loadInstitutionTypes: InstitutionTypeActions.IInstitutionTypeLoadActionCreator;
    filter: InstitutionTypeActions.IInstitutionTypeFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
}

const InstitutionTypesPage: FC<InstitutionTypesPageProps> = ({
    formFilter,
    institutionType,
    loadingInstitutionTypes,
    loadInstitutionTypesSuccess,
    loadInstitutionTypesFailure,
    loadInstitutionTypesErrorCode,
    permissions,
    pageSize,
    clearInstitutionTypes,
    loadInstitutionTypes,
    filter,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showInstitutionTypes =
        !loadingInstitutionTypes && loadInstitutionTypesSuccess;


    // Load institution types
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsInstitutionsSubMenu(permissions));
            setBreadcrumbs(InstitutionTypesBreadcrumbs());

            loadInstitutionTypes();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsInstitutionsSubMenu,
        permissions,
        setBreadcrumbs,
        InstitutionTypesBreadcrumbs,
        loadInstitutionTypes,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionTypesFailure) {
            navigate(`/error/${loadInstitutionTypesErrorCode}`);
        }
    }, [loadInstitutionTypesFailure, loadInstitutionTypesErrorCode, navigate]);

    // Cleanup effect to clear the institution types on component unmount
    useEffect(() => {
        return () => {
            clearInstitutionTypes();
        };
    }, [clearInstitutionTypes]);

    const createTable = useMemo(() => {
        if (!institutionType || institutionType.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no institution types to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = institutionType.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={institutionType}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : institutionType.length
                    }
                    pageSize={
                        showPagination ? pageSize : institutionType.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no institution types to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (e, handleOriginal) => {
                                const url: string = `/settings/institution/institutiontypes/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [institutionType, pageSize, PAGE_SIZE_OPTIONS, setPageSize, navigate]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Institution Types</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.InstitutionTypesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={
                                    "/settings/institution/institutiontypes/create"
                                }
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showInstitutionTypes ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showInstitutionTypes ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [formFilter, filter, permissions, showInstitutionTypes, createTable]);

    return (
        <InstitutionLookupsLayout
            loading={loadingInstitutionTypes}
            permission={Dtos.Permission.InstitutionTypesView}
        >
            {renderContent}
        </InstitutionLookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<InstitutionTypesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        formFilter: state.institutionTypes.formFilter,
        institutionType: state.institutionTypes.formFilteredData,
        loadingInstitutionTypes:
            state.institutionTypes.loadState &&
            state.institutionTypes.loadState.status == RequestState.Pending,
        loadInstitutionTypesSuccess:
            state.institutionTypes.loadState &&
            state.institutionTypes.loadState.status == RequestState.Success,
        loadInstitutionTypesFailure:
            state.institutionTypes.loadState &&
            state.institutionTypes.loadState.status == RequestState.Failure,
        loadInstitutionTypesErrorCode:
            state.institutionTypes.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<InstitutionTypesPageProps> => {
    return {
        loadInstitutionTypes: bindActionCreators(
            InstitutionTypeActions.LoadInstitutionType,
            dispatch
        ),
        clearInstitutionTypes: bindActionCreators(
            InstitutionTypeActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(InstitutionTypeActions.Filter, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(InstitutionTypesPage);
